import type { ReviewerLoad, TierFit } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { DataTable } from '@eriko/ui/src/components/ui/data-table';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  useAdminQueue,
  useAssignReviewer,
  useBulkSignoff,
  type QueueRow,
} from '../hooks/useAdminQueue';
import { useAuthStore } from '../stores/authStore';

function periodStatusLabel(status: string | null): {
  label: string;
  variant: 'secondary' | 'warning' | 'info' | 'success' | 'outline';
} {
  switch (status) {
    case 'READY_FOR_REVIEW':
      return { label: 'Ready', variant: 'warning' };
    case 'IN_REVIEW':
      return { label: 'In Progress', variant: 'info' };
    case 'APPROVED':
    case 'DELIVERED':
      return { label: 'Done', variant: 'success' };
    case 'PROCESSING':
      return { label: 'Processing', variant: 'secondary' };
    case null:
      return { label: 'No close yet', variant: 'outline' };
    default:
      return { label: status ?? '—', variant: 'outline' };
  }
}

/** B3 tier-fit → an inline badge (no badge for a GOOD fit). */
function tierFitDisplay(tf: TierFit): { label: string; className: string; title: string } | null {
  if (tf.status === 'GOOD') return null;
  const title = tf.reasons.join(' · ');
  return tf.status === 'OVER_TIER'
    ? { label: 'Over tier', className: 'bg-neg-surface text-neg', title }
    : { label: 'Watch', className: 'bg-warn-surface text-warn', title };
}

/** A close is bulk-sign-off-eligible when it exists and is review-ready. */
function isSignoffEligible(row: QueueRow): boolean {
  return (
    row.closeId != null &&
    (row.periodStatus === 'READY_FOR_REVIEW' || row.periodStatus === 'IN_REVIEW')
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex-1 min-w-[120px] bg-surface-0 border border-surface-300 rounded-lg px-4 py-3">
      <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400">
        {label}
      </div>
      <div className="font-mono text-[22px] font-medium mt-0.5 text-surface-900">{value}</div>
    </div>
  );
}

function CapacityChip({ load }: { load: ReviewerLoad }) {
  const tone =
    load.status === 'AT_CAP'
      ? 'bg-neg-surface text-neg'
      : load.status === 'NEAR'
        ? 'bg-warn-surface text-warn'
        : 'bg-surface-100 text-surface-600';
  const suffix =
    load.status === 'AT_CAP' ? ' · at capacity' : load.status === 'NEAR' ? ' · near cap' : '';
  return (
    <span className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${tone}`}>
      My load: {load.assigned}/{load.cap}
      {suffix}
    </span>
  );
}

const checkboxClass =
  'h-3.5 w-3.5 rounded border-surface-300 accent-surface-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40';

export function ReviewQueuePage() {
  const navigate = useNavigate();
  const currentUserId = useAuthStore((s) => s.user)?.id ?? null;
  const [mine, setMine] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { data, isLoading, error } = useAdminQueue(mine);
  const assign = useAssignReviewer();
  const bulk = useBulkSignoff();

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const counters = data?.counters;
  const myLoad = data?.myLoad;

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aActive =
        a.openConvCount > 0 ||
        a.periodStatus === 'READY_FOR_REVIEW' ||
        a.periodStatus === 'IN_REVIEW';
      const bActive =
        b.openConvCount > 0 ||
        b.periodStatus === 'READY_FOR_REVIEW' ||
        b.periodStatus === 'IN_REVIEW';
      if (aActive !== bActive) return aActive ? -1 : 1;
      return a.organizationName.localeCompare(b.organizationName);
    });
  }, [rows]);

  // B1 — claim (assign to self); on 409 CAPACITY_EXCEEDED, confirm the override.
  function claim(row: QueueRow, force = false) {
    assign.mutate(
      { organizationId: row.organizationId, force },
      {
        onSuccess: () => toast.success(`Assigned ${row.organizationName} to you`),
        onError: (e) => {
          const err = e as Error & { code?: string };
          if (err.code === 'CAPACITY_EXCEEDED' && !force) {
            if (window.confirm("You're at capacity. Assign this client anyway?")) claim(row, true);
            return;
          }
          toast.error(err.message || 'Failed to assign');
        },
      },
    );
  }

  const col = createColumnHelper<QueueRow>();
  const columns = [
    col.display({
      id: 'select',
      size: 36,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Select all review-ready"
          className={checkboxClass}
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) =>
        row.getCanSelect() ? (
          <input
            type="checkbox"
            aria-label="Select for bulk sign-off"
            className={checkboxClass}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ) : null,
    }),
    col.accessor('organizationName', {
      header: 'Client',
      size: 220,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            <div className="font-semibold text-surface-900">{info.getValue()}</div>
            <div className="text-[10px] text-surface-500">{row.organizationSlug}</div>
          </div>
        );
      },
    }),
    col.accessor('tier', {
      header: 'Tier',
      size: 70,
      cell: (info) => <span className="text-[11px] text-surface-600">{info.getValue()}</span>,
    }),
    col.accessor('tierFit', {
      header: 'Fit',
      size: 90,
      cell: (info) => {
        const d = tierFitDisplay(info.getValue());
        return d ? (
          <span
            title={d.title}
            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${d.className}`}
          >
            {d.label}
          </span>
        ) : (
          <span className="text-surface-300 text-[11px]">—</span>
        );
      },
    }),
    col.accessor('periodStatus', {
      header: 'Period',
      size: 110,
      cell: (info) => {
        const { label, variant } = periodStatusLabel(info.getValue());
        return <Badge variant={variant}>{label}</Badge>;
      },
    }),
    col.accessor('timeSpentMinutes', {
      header: () => <span className="text-center block">Time</span>,
      size: 60,
      cell: (info) => (
        <span className="block text-center font-mono text-[11px] text-surface-600">
          {info.getValue() != null ? `${Math.round(info.getValue()!)}m` : '—'}
        </span>
      ),
    }),
    col.accessor('flagCount', {
      header: () => <span className="text-center block">Flags</span>,
      size: 60,
      cell: (info) => {
        const v = info.getValue();
        return (
          <span
            className={`block text-center font-mono text-[10px] font-semibold ${v ? 'text-neg' : 'text-surface-400'}`}
          >
            {v || '—'}
          </span>
        );
      },
    }),
    col.accessor('openConvCount', {
      header: () => <span className="text-center block">Open convs</span>,
      size: 90,
      cell: (info) => {
        const v = info.getValue();
        return (
          <span
            className={`block text-center font-mono text-[10px] font-semibold ${v ? 'text-warn' : 'text-surface-400'}`}
          >
            {v || '—'}
          </span>
        );
      },
    }),
    col.accessor('reviewer', {
      header: 'Assigned',
      size: 140,
      cell: (info) => {
        const r = info.getValue();
        const row = info.row.original;
        const mineRow = !!r?.id && r.id === currentUserId;
        if (mineRow) {
          return <span className="text-[11px] font-medium text-surface-900">You</span>;
        }
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {r && <span className="text-surface-600 text-[11px] truncate">{r.fullName ?? '—'}</span>}
            <button
              type="button"
              onClick={() => claim(row)}
              disabled={assign.isPending}
              className="px-2 py-0.5 rounded-md border border-surface-300 text-[11px] font-medium text-surface-700 hover:border-surface-500 hover:text-surface-900 disabled:opacity-50 cursor-pointer"
            >
              {r ? 'Reassign to me' : 'Claim'}
            </button>
          </div>
        );
      },
    }),
  ];

  // TanStack Table's useReactTable returns memoizable values that React Compiler
  // flags as "incompatible-library" — known false positive. Suppression is
  // per-callsite; safe to remove once the rule recognizes the library.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: sorted,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => isSignoffEligible(row.original),
    getRowId: (row) => row.organizationId,
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedCloseIds = table
    .getSelectedRowModel()
    .rows.map((r) => r.original.closeId)
    .filter((id): id is string => id != null);

  function runBulkSignoff() {
    if (selectedCloseIds.length === 0) return;
    bulk.mutate(selectedCloseIds, {
      onSuccess: (res) => {
        const parts = [`${res.signedOff.length} signed off`];
        if (res.blocked.length) parts.push(`${res.blocked.length} blocked (open items)`);
        if (res.skipped.length) parts.push(`${res.skipped.length} skipped`);
        if (res.blocked.length) toast.warning(parts.join(' · '));
        else toast.success(parts.join(' · '));
        setRowSelection({});
      },
      onError: (e) => toast.error((e as Error).message || 'Bulk sign-off failed'),
    });
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-6 pt-5 shrink-0 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-surface-900">Review Queue</h1>
          <p className="text-xs text-surface-500 mt-0.5">
            {mine
              ? 'Clients assigned to you — click a row to open their view.'
              : 'All active clients — click a row to open their view.'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {myLoad && <CapacityChip load={myLoad} />}
          <button
            type="button"
            onClick={() => {
              setMine((m) => !m);
              setRowSelection({});
            }}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium border cursor-pointer transition-colors ${
              mine
                ? 'bg-surface-900 border-surface-900 text-white'
                : 'border-surface-300 text-surface-700 hover:border-surface-500'
            }`}
          >
            {mine ? 'My clients' : 'All clients'}
          </button>
        </div>
      </div>

      {counters && (
        <div className="flex gap-2 px-6 pt-4 overflow-x-auto">
          <Counter label="Awaiting" value={counters.awaiting} />
          <Counter label="In Progress" value={counters.inProgress} />
          <Counter label="Open flags" value={counters.openFlags} />
          <Counter label="Signed Off" value={counters.signedOff} />
          <Counter label="Open convs" value={counters.openConvs} />
          <Counter label="Clients" value={counters.clients} />
        </div>
      )}

      {/* B5 — bulk action bar (appears when review-ready closes are selected). */}
      {selectedCloseIds.length > 0 && (
        <div className="mx-6 mt-4 flex items-center justify-between rounded-lg border border-surface-300 bg-surface-100 px-4 py-2.5">
          <span className="text-xs font-medium text-surface-700">
            {selectedCloseIds.length} review-ready{' '}
            {selectedCloseIds.length === 1 ? 'close' : 'closes'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRowSelection({})}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-surface-600 hover:text-surface-900 cursor-pointer"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={runBulkSignoff}
              disabled={bulk.isPending}
              className="px-3 py-1 rounded-md bg-surface-900 text-white text-[11px] font-semibold hover:bg-surface-700 disabled:opacity-50 cursor-pointer"
            >
              {bulk.isPending ? 'Signing off…' : `Sign off ${selectedCloseIds.length}`}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto px-6 pt-4 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-900 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="rounded-lg bg-neg-surface border border-neg-border px-4 py-3">
            <p className="text-xs text-neg">{(error as Error).message}</p>
          </div>
        ) : (
          <DataTable
            table={table}
            onRowClick={(row) => navigate(`/orgs/${row.organizationId}`)}
            emptyMessage={mine ? 'No clients assigned to you.' : 'No clients yet.'}
          />
        )}
      </div>
    </div>
  );
}
